import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Dashboard/Header/Header';
import Sidebar from '../../components/Dashboard/Sidebar/Sidebar';
import Breadcrumbs from '../../components/Common/Breadcrumbs/Breadcrumbs';
import Pagination from '../../components/Common/Pagination/Pagination';
import {
  Download,
  Search,
  Eye,
  Edit3,
  MoreVertical,
  Plus,
  IndianRupee,
  CheckCircle2,
  Clock,
  Calendar as CalendarIcon,
  Loader2,
  X,
  ChevronDown,
  ChevronUp,
  LayoutDashboard,
  SlidersHorizontal as Filters,
  Pencil,
  Trash2,
  Filter
} from 'lucide-react';
import CustomSelect from '../../components/Common/Select/CustomSelect';
import styles from './PaymentsReceived.module.css';
import PaymentReceivedDrawer from './PaymentReceivedDrawer';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import Footer from '../../components/Common/Footer/Footer';
import NoData from '../../components/Common/NoData/NoData';
import { useLoading } from '../../context/LoadingContext';

const MySwal = withReactContent(Swal);

const PaymentsReceived = () => {
  const { loading, showLoader, hideLoader } = useLoading();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [payments, setPayments] = useState([]);
  const [pagination, setPagination] = useState({ total: 0 });
  const [openMenuIndex, setOpenMenuIndex] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [showFilters, setShowFilters] = useState(false);
  const [isSummaryOpen, setIsSummaryOpen] = useState(false);
  const [summaryData, setSummaryData] = useState({
    total_work_order_value: 0,
    total_received: 0,
    outstanding_amount: 0,
    last_payment_date: null
  });
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [filters, setFilters] = useState({
    project_id: '',
    payment_mode: ''
  });
  const [filterData, setFilterData] = useState({
    projects: []
  });
  const navigate = useNavigate();
  const mainContentRef = React.useRef(null);
  const isInitialMount = React.useRef(true);

  // Permission guards
  const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
  const userPermissions = storedUser.permissions || [];
  const isAdmin = (storedUser.role || '').toLowerCase() === 'admin';
  const canCreate = isAdmin || userPermissions.includes('create payments received');
  const canEdit = isAdmin || userPermissions.includes('edit payments received');
  const canDelete = isAdmin || userPermissions.includes('delete payments received');

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  React.useEffect(() => {
    fetchPayments();
    fetchSummary();
  }, [currentPage, itemsPerPage, searchTerm, filters]);

  React.useEffect(() => {
    fetchFilterData();
  }, []);

  const fetchFilterData = async () => {
    try {
      const projectsRes = await api.get('/projects');
      setFilterData({
        projects: projectsRes.data.data.data || []
      });
    } catch (error) {
      console.error('Error fetching filter data:', error);
    }
  };

  const fetchPayments = async () => {
    showLoader();
    try {
      const response = await api.get('/customer-payments', {
        params: {
          page: currentPage,
          per_page: itemsPerPage,
          search: searchTerm,
          ...filters
        }
      });
      if (response.data.success) {
        setPayments(response.data.data.data);
        setPagination({
          total: response.data.data.total,
          current_page: response.data.data.current_page,
          per_page: response.data.data.per_page
        });
      }
    } catch (error) {
      console.error('Error fetching payments:', error);
      toast.error('Failed to load payments');
    } finally {
      hideLoader();
    }
  };

  const fetchSummary = async () => {
    setSummaryLoading(true);
    try {
      const response = await api.get('/payments-received/summary', {
        params: {
          search: searchTerm,
          ...filters
        }
      });
      if (response.data.success) {
        setSummaryData(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching summary:', error);
    } finally {
      setSummaryLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setFilters({
      project_id: '',
      payment_mode: ''
    });
    setCurrentPage(1);
  };

  const hasActiveFilters = filters.project_id || filters.payment_mode !== '';

  const handleDelete = async (payment) => {
    setOpenMenuIndex(null);

    MySwal.fire({
      showConfirmButton: true,
      showCancelButton: true,
      confirmButtonText: '<div style="display: flex; align-items: center; justify-content: center; gap: 8px;"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"></path><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg> Delete Record</div>',
      cancelButtonText: 'Cancel',
      buttonsStyling: false,
      customClass: {
        popup: 'custom-swal-popup',
        actions: 'custom-swal-actions',
        confirmButton: 'custom-swal-confirm',
        cancelButton: 'custom-swal-cancel',
      },
      html: `
        <style>
          .custom-swal-popup {
            border-radius: 16px !important;
            padding: 32px 24px 24px 24px !important;
            font-family: 'Outfit', sans-serif !important;
            width: 440px !important;
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
          }
          @media (max-width: 480px) {
            .custom-swal-popup {
              width: calc(100vw - 32px) !important;
              padding: 24px 16px 20px 16px !important;
            }
            .custom-swal-actions {
              flex-direction: column !important;
              gap: 8px !important;
            }
            .custom-swal-confirm,
            .custom-swal-cancel {
              width: 100% !important;
              justify-content: center !important;
            }
          }
          .custom-swal-actions {
            display: flex !important;
            gap: 12px !important;
            margin-top: 24px !important;
            width: 100% !important;
            padding: 0 !important;
          }
          .custom-swal-confirm {
            background-color: #dc2626 !important;
            color: white !important;
            border: none !important;
            border-radius: 8px !important;
            padding: 10px 24px !important;
            font-size: 14px !important;
            font-weight: 600 !important;
            flex: 1 !important;
            cursor: pointer !important;
            transition: all 0.2s !important;
          }
          .custom-swal-confirm:hover {
            background-color: #b91c1c !important;
          }
          .custom-swal-cancel {
            background-color: #ffffff !important;
            color: #475569 !important;
            border: 1px solid #cbd5e1 !important;
            border-radius: 8px !important;
            padding: 10px 24px !important;
            font-size: 14px !important;
            font-weight: 600 !important;
            flex: 1 !important;
            cursor: pointer !important;
            transition: all 0.2s !important;
          }
          .custom-swal-cancel:hover {
            background-color: #f8fafc !important;
          }
          .custom-icon-wrapper {
            width: 64px;
            height: 64px;
            background-color: #fee2e2;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 20px;
          }
        </style>
        <div class="custom-icon-wrapper">
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#dc2626" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path>
            <line x1="12" y1="9" x2="12" y2="13"></line>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
        </div>
        <h2 style="font-size: 20px; font-weight: 700; color: #0f172a; margin: 0 0 12px 0;">Delete Record?</h2>
        <p style="margin: 0 0 24px 0; color: #475569; font-size: 14px; line-height: 1.5;">
          Are you sure you want to delete payment record <strong>${payment.ref_transaction_number || 'REC-' + payment.id}</strong>?<br/>
          This action cannot be undone.
        </p>
      `
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await api.delete(`/customer-payments/${payment.id}`);
          if (response.data.success) {
            toast.success('Payment record deleted successfully');
            fetchPayments();
            fetchSummary();
          }
        } catch (error) {
          console.error('Error deleting payment:', error);
          toast.error(error.response?.data?.message || 'Failed to delete payment');
        }
      }
    });
  };

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.action-wrapper')) {
        setOpenMenuIndex(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  React.useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    const scrollTimeout = setTimeout(() => {
      if (mainContentRef.current) {
        mainContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 50);
    return () => clearTimeout(scrollTimeout);
  }, [currentPage]);

  const toggleActionMenu = (index, e) => {
    if (openMenuIndex === index) {
      setOpenMenuIndex(null);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      setMenuPosition({ top: rect.bottom + 4, left: rect.right - 140 });
      setOpenMenuIndex(index);
    }
  };

  const metrics = [
    {
      label: 'Total Work Order Value',
      value: `₹${parseFloat(summaryData.total_work_order_value || 0).toLocaleString('en-IN')}`,
      icon: <IndianRupee size={20} />,
      theme: 'blue'
    },
    {
      label: 'Total Amount Received',
      value: `₹${parseFloat(summaryData.total_received || 0).toLocaleString('en-IN')}`,
      icon: <CheckCircle2 size={20} />,
      theme: 'green',
      highlight: true
    },
    {
      label: 'Outstanding Amount',
      value: `₹${parseFloat(summaryData.outstanding_amount || 0).toLocaleString('en-IN')}`,
      icon: <Clock size={20} />,
      theme: 'orange'
    },
    {
      label: 'Last Payment Date',
      value: summaryData.last_payment_date
        ? new Date(summaryData.last_payment_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
        : '---',
      icon: <CalendarIcon size={20} />,
      theme: 'purple'
    }
  ];

  const paymentData = [
    {
      id: 'REC-10294',
      project: 'Skyline residency - Block A',
      woNumber: 'WO-23-005',
      date: '24 Oct 2023',
      type: 'CASH',
      bankDetails: { name: '---', cheque: '' },
      amount: '₹50,000.00',
      enteredBy: 'R. Sharma'
    },
    {
      id: 'REC-10293',
      project: 'Metro Hub Phase 2',
      woNumber: 'WO-23-042',
      date: '22 Oct 2023',
      type: 'CHEQUE',
      bankDetails: { name: 'HDFC Bank', cheque: '#458291' },
      amount: '₹4,25,000.00',
      enteredBy: 'M. Varma'
    },
    {
      id: 'REC-10292',
      project: 'Green Valley Estate',
      woNumber: 'WO-23-018',
      date: '20 Oct 2023',
      type: 'CHEQUE',
      bankDetails: { name: 'ICICI Bank', cheque: '#110293' },
      amount: '₹12,50,000.00',
      enteredBy: 'R. Sharma'
    },
    {
      id: 'REC-10291',
      project: 'Skyline residency - Block A',
      woNumber: 'WO-23-005',
      date: '15 Oct 2023',
      type: 'CASH',
      bankDetails: { name: '---', cheque: '' },
      amount: '₹2,00,000.00',
      enteredBy: 'M. Varma'
    }
  ];

  return (
    <div className={`${styles['payments-received-container']} ${isSidebarOpen ? styles['sidebar-open'] : ''}`}>
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={toggleSidebar} />

        <main className={styles['main-content']} ref={mainContentRef}>
          <Breadcrumbs />

          <div className={styles['page-header']}>
            <div className={styles['header-title-section']}>
              <h1>Payment Received</h1>
              <p>Manage and track all work order payments and outstanding balances.</p>
            </div>
            <div className={styles['header-actions-top']}>
              <button className={styles['control-btn']}>
                <Download size={18} />
                <span>Export</span>
              </button>
              {canCreate && (
                <button className={styles['add-btn']} onClick={() => {
                  setEditingId(null);
                  setIsDrawerOpen(true);
                }}>
                  <Plus size={20} />
                  <span>Add Payment</span>
                </button>
              )}
            </div>
          </div>

          <div className={styles['summary-collapsible']}>
            <button
              className={styles['summary-toggle']}
              onClick={() => setIsSummaryOpen(!isSummaryOpen)}
            >
              <div className={styles['summary-toggle-left']}>
                <LayoutDashboard size={20} style={{ color: '#f97316' }} />
                <div className={styles['summary-toggle-text']}>
                  <span className={styles['summary-toggle-title']}>Summary</span>
                  <span className={styles['summary-toggle-hint']}>
                    {metrics[1]?.value} received · {metrics[2]?.value} outstanding
                  </span>
                </div>
              </div>
              {isSummaryOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>

            <div className={`${styles['summary-metrics-row']} ${isSummaryOpen ? styles['summary-expanded'] : styles['summary-collapsed']}`}>
              {metrics.map((metric, index) => (
                <div key={index} className={`${styles['metric-card']} ${styles[metric.theme]} ${summaryLoading ? styles['loading'] : ''} ${metric.highlight ? styles['highlight-card'] : ''}`}>
                  <div className={styles['metric-icon-box']}>
                    {metric.icon}
                  </div>
                  <div className={styles['metric-info']}>
                    <span className={styles['metric-label']}>{metric.label}</span>
                    {summaryLoading ? (
                      <div className={styles['shimmer-value']}></div>
                    ) : (
                      <div className={styles['metric-value-group']}>
                        <span className={styles['metric-value']}>{metric.value}</span>
                      </div>
                    )}
                  </div>
                  {metric.highlight && <div className={styles['glow-effect']}></div>}
                </div>
              ))}
            </div>
          </div>

          <div className={styles['table-controls']}>
            <div className={styles['search-wrapper']}>
              <Search className={styles['search-icon']} size={18} />
              <input
                type="text"
                placeholder="Search by Invoice, Project or Reference..."
                className={styles['search-input']}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className={styles['filter-actions']}>
              <div className={styles['per-page-select-wrapper']}>
                <CustomSelect
                  isSearchable={false}
                  backgroundColor="white"
                  value={{ value: itemsPerPage, label: `${itemsPerPage} per page` }}
                  onChange={(option) => { setItemsPerPage(Number(option.value)); setCurrentPage(1); }}
                  options={[
                    { value: 10, label: '10 per page' },
                    { value: 25, label: '25 per page' },
                    { value: 50, label: '50 per page' },
                    { value: 100, label: '100 per page' }
                  ]}
                />
              </div>
              <button
                className={`${styles['filter-btn']} ${showFilters ? styles['active'] : ''}`}
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filters size={18} />
                <span>Filters</span>
                {hasActiveFilters && <span className={styles['filter-badge']}></span>}
              </button>
            </div>
          </div>

          {showFilters && (
            <div className={styles['filters-panel']}>
              <div className={styles['filters-header']}>
                <div className={styles['filters-title-group']}>
                  <h3>Filter Payments Received</h3>
                  <p>Filter records by project or payment method.</p>
                </div>
                <button className={styles['close-icon-btn']} onClick={() => setShowFilters(false)}>
                  <X size={18} />
                </button>
              </div>
              <div className={styles['filters-grid']}>
                <div className={styles['filter-group']}>
                  <label>Project</label>
                  <CustomSelect
                    value={filters.project_id ? {
                      value: filters.project_id,
                      label: filterData.projects.find(d => d.id === filters.project_id)?.project_name
                    } : null}
                    onChange={(opt) => handleFilterChange('project_id', opt ? opt.value : '')}
                    options={filterData.projects.map(d => ({ value: d.id, label: d.project_name }))}
                    placeholder="All Projects"
                    isClearable={true}
                  />
                </div>
                <div className={styles['filter-group']}>
                  <label>Payment Mode</label>
                  <CustomSelect
                    value={filters.payment_mode ? {
                      value: filters.payment_mode,
                      label: ['Cash', 'Bank Transfer', 'Cheque', 'Online'][filters.payment_mode - 1]
                    } : null}
                    onChange={(opt) => handleFilterChange('payment_mode', opt ? opt.value : '')}
                    options={[
                      { value: 1, label: 'Cash' },
                      { value: 2, label: 'Bank Transfer' },
                      { value: 3, label: 'Cheque' },
                      { value: 4, label: 'Online' }
                    ]}
                    placeholder="All Modes"
                    isClearable={true}
                  />
                </div>
              </div>
              <div className={styles['filters-footer']}>
                <button className={styles['reset-btn']} onClick={resetFilters}>Reset</button>
                <button className={styles['close-btn']} onClick={() => setShowFilters(false)}>Close Filters</button>
              </div>
            </div>
          )}

          <div className={styles['data-view-container']}>
            {/* Desktop View */}
            <div className={styles['table-wrapper']}>
              <table className={styles['payment-table']}>
                <thead>
                  <tr>
                    <th>RECEIPT & ID</th>
                    <th>PROJECT</th>
                    <th>SALES INVOICE#</th>
                    <th>TOTAL AMOUNT</th>
                    <th>DATE</th>
                    <th>TYPE</th>
                    <th>BANK DETAILS</th>
                    <th>RECEIVED AMOUNT</th>
                    <th>ENTERED BY</th>
                    <th className={styles['actions-cell']}>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.length === 0 ? (
                    !loading && <NoData message="No payments found." colSpan={10} isTable={true} />
                  ) : payments.map((item, index) => (
                    <tr key={index}>
                      <td className={styles['receipt-cell']}>
                        <div className={styles['receipt-info']}>
                          <span className={styles['receipt-text']}>{item.ref_transaction_number || 'REC-' + item.id}</span>
                        </div>
                      </td>
                      <td className={styles['project-name']}>{item.project?.project_name}</td>
                      <td className={styles['wo-number']}>{item.sales_invoice?.invoice_number}</td>
                      <td className={styles['amount-cell']} style={{ fontWeight: 600 }}>₹{parseFloat(item.sales_invoice?.total_amount || 0).toLocaleString()}</td>
                      <td>{new Date(item.payment_date).toLocaleDateString()}</td>
                      <td>
                        <span className={`${styles['status-pill']} ${['cash', 'bank', 'cheque', 'online'][item.payment_mode - 1]?.toLowerCase() === 'cash' ? styles['active'] : styles['inactive']}`}>
                          {['Cash', 'Bank Transfer', 'Cheque', 'Online'][item.payment_mode - 1]}
                        </span>
                      </td>
                      <td className={styles['bank-details-cell']}>
                        <span className={styles['bank-name']}>{item.bank_account?.bank_name || '---'}</span>
                        {item.cheque_date && (
                          <span className={styles['cheque-info']}>{new Date(item.cheque_date).toLocaleDateString()}</span>
                        )}
                      </td>
                      <td className={styles['amount-cell']}>₹{parseFloat(item.amount).toLocaleString()}</td>
                      <td>{item.creator?.name || 'System'}</td>
                      <td className={styles['actions-cell']}>
                        <div className={`action-wrapper ${styles['action-wrapper']}`}>
                          <button
                            className={`${styles['action-btn']} ${openMenuIndex === index ? styles['active'] : ''}`}
                            onClick={(e) => toggleActionMenu(index, e)}
                          >
                            <MoreVertical size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile View */}
            <div className={styles['payment-cards']}>
              {payments.length === 0 ? (
                !loading && <NoData message="No payments found." isTable={false} />
              ) : payments.map((item, index) => (
                <div key={index} className={styles['payment-card']}>
                  <div className={styles['card-main']}>
                    <div className={styles['card-info']}>
                      <span className={styles['card-id']}>{item.ref_transaction_number || 'REC-' + item.id}</span>
                      <span className={styles['card-amount']}>₹{parseFloat(item.amount).toLocaleString()}</span>
                    </div>
                    <div className={`action-wrapper ${styles['action-wrapper']}`}>
                      <button
                        className={styles['action-btn']}
                        onClick={(e) => toggleActionMenu(index, e)}
                      >
                        <MoreVertical size={18} />
                      </button>
                    </div>
                  </div>
                  <div className={styles['card-details']}>
                    <div className={styles['detail-item']} style={{ gridColumn: 'span 2' }}>
                      <span className={styles['detail-label']}>Project</span>
                      <span className={styles['detail-value']}>{item.project?.project_name}</span>
                    </div>
                    <div className={styles['detail-item']}>
                      <span className={styles['detail-label']}>Invoice#</span>
                      <span className={styles['detail-value']}>{item.sales_invoice?.invoice_number || '---'}</span>
                    </div>
                    <div className={styles['detail-item']}>
                      <span className={styles['detail-label']}>Total Inv.</span>
                      <span className={styles['detail-value']}>₹{parseFloat(item.sales_invoice?.total_amount || 0).toLocaleString()}</span>
                    </div>
                    <div className={styles['detail-item']}>
                      <span className={styles['detail-label']}>Entered by</span>
                      <span className={styles['detail-value']}>{item.creator?.name || 'System'}</span>
                    </div>
                  </div>
                  <div className={styles['card-footer']}>
                    <div className={styles['footer-left']}>
                      <CalendarIcon size={14} className={styles['text-muted']} />
                      <span>{new Date(item.payment_date).toLocaleDateString()}</span>
                    </div>
                    <span className={`${styles['status-pill']} ${['cash', 'bank', 'cheque', 'online'][item.payment_mode - 1]?.toLowerCase() === 'cash' ? styles['active'] : styles['inactive']}`}>
                      {['Cash', 'Bank Transfer', 'Cheque', 'Online'][item.payment_mode - 1]}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <div className={styles['desktop-pagination']}>
              <Pagination
                currentPage={currentPage}
                totalItems={pagination.total}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
              />
            </div>
          </div>

          <div className={styles['mobile-pagination']}>
            <Pagination
              currentPage={currentPage}
              totalItems={pagination.total}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
            />
          </div>

          {/* Global Fixed Action Menu */}
          {openMenuIndex !== null && (
            <div
              className={[styles['action-dropdown'], styles['shadow-premium']].join(' ')}
              style={{
                position: 'fixed',
                top: `${menuPosition.top}px`,
                left: `${menuPosition.left}px`,
                zIndex: 9999
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button className={styles['dropdown-item']} onClick={() => navigate(`/payment/payments-received/view/${payments[openMenuIndex]?.id}`)}>
                <Eye size={16} />
                <span>View Receipt</span>
              </button>
              {canEdit && (
                <button className={styles['dropdown-item']} onClick={() => {
                  setEditingId(payments[openMenuIndex]?.id);
                  setIsDrawerOpen(true);
                  setOpenMenuIndex(null);
                }}>
                  <Pencil size={16} />
                  <span>Edit</span>
                </button>
              )}
              {canDelete && (
                <button className={[styles['dropdown-item'], styles['deselect']].join(' ')} onClick={() => handleDelete(payments[openMenuIndex])}>
                  <Trash2 size={16} />
                  <span>Delete</span>
                </button>
              )}
              <div className={styles['dropdown-divider']}></div>
              <button className={[styles['dropdown-item'], styles['deselect']].join(' ')}>
                <Download size={16} />
                <span>Download PDF</span>
              </button>
            </div>
          )}
          <PaymentReceivedDrawer
            isOpen={isDrawerOpen}
            onClose={() => {
              setIsDrawerOpen(false);
              setEditingId(null);
            }}
            onSave={fetchPayments}
            paymentId={editingId}
          />

          <Footer />
        </main>
      </div>
    </div>
  );
};

export default PaymentsReceived;
